import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { auditAPI, entitiesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { History, Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function AuditTrailPage() {
  const { isDPO } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(searchParams.get('entity') || '');
  const [currentPage, setCurrentPage] = useState((Number(searchParams.get('page')) || 1) - 1); // URL is 1-based, state is 0-based
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 50);

  // Fetch entities once on mount (DPO only)
  useEffect(() => {
    if (isDPO) {
      fetchEntities();
    }
  }, [isDPO]);

  // Update URL params when pagination state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage > 0) params.set('page', String(currentPage + 1)); // Convert 0-based state to 1-based URL
    if (itemsPerPage !== 50) params.set('limit', String(itemsPerPage));
    if (selectedEntity) params.set('entity', selectedEntity);
    setSearchParams(params);
  }, [selectedEntity, currentPage, itemsPerPage, setSearchParams]);

  // Fetch audit logs on pagination/filter changes
  useEffect(() => {
    fetchAuditLogs();
  }, [selectedEntity, currentPage, itemsPerPage]);

  const fetchEntities = async () => {
    try {
      const response = await entitiesAPI.getAll();
      setEntities(response.data);
    } catch (error) {
      console.error('Failed to fetch entities:', error);
      toast.error('Failed to fetch entities');
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const skip = currentPage * itemsPerPage;
      const logsRes = await auditAPI.getAll(selectedEntity || undefined, itemsPerPage, skip);
      console.log('Audit logs response:', logsRes);
      setLogs(logsRes.data.data);
      setTotalCount(logsRes.data.total);
      setHasMore(logsRes.data.hasMore);
    } catch (error) {
      console.error('Audit logs error:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = (logs && Array.isArray(logs)) ? logs.filter(log => {
    if (!log) return false;
    const search = searchTerm.toLowerCase();
    return (
      String(log?.user_email || '').toLowerCase().includes(search) ||
      String(log?.action || '').toLowerCase().includes(search) ||
      String(log?.entity_type || '').toLowerCase().includes(search)
    );
  }) : [];


  const actionColors = {
    create: 'success',
    update: 'warning',
    delete: 'destructive',
    login: 'secondary',
    register: 'secondary'
  };

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'Details'].join(','),
      ...filteredLogs.map(log => [
        format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss'),
        log.user_email,
        log.action,
        log.resource_type,
        log.resource_id || '',
        log.details || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="audit-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Audit Trail</h1>
            <p className="text-muted-foreground">Track all system activities</p>
          </div>
          <Button variant="outline" onClick={exportLogs} data-testid="export-audit-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search logs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
          </div>
          {isDPO && (
            <Select value={selectedEntity || "all"} onValueChange={(val) => {
              setSelectedEntity(val === "all" ? "" : val);
              setCurrentPage(0);
            }}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="All Entities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entities.map(entity => (<SelectItem key={entity.id} value={entity.id}>{entity.entity_name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  {isDPO && <TableHead>Entity / Client</TableHead>}
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={isDPO ? 6 : 5} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow><TableCell colSpan={isDPO ? 6 : 5} className="text-center py-8 text-muted-foreground">No audit logs found</TableCell></TableRow>
                ) : (
                  filteredLogs.filter(log => log && typeof log === 'object' && log.id).map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">
                        {log?.timestamp ? format(new Date(log.timestamp), 'MMM d, yyyy HH:mm') : '-'}
                      </TableCell>
                      <TableCell>{String(log?.user_email || 'unknown')}</TableCell>
                      <TableCell>
                        <Badge variant={actionColors[log?.action] || 'secondary'}>{log?.action || 'unknown'}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{log?.entity_type || '-'}</span>
                        {log?.entity_id && <span className="text-xs text-muted-foreground ml-1">({String(log.entity_id).slice(0, 8)}...)</span>}
                      </TableCell>
                      {isDPO && <TableCell>{log?.entity_name || '-'}</TableCell>}
                      <TableCell className="max-w-xs truncate text-muted-foreground">
                        {typeof log?.details === 'object' ? (log.details?.message || JSON.stringify(log.details)) : (log?.details || '-')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination Controls */}
        {totalCount > 0 && (
          <div className="flex flex-col gap-4 p-4 bg-muted rounded-lg">
            {/* Summary and Items Per Page */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalCount)} of {totalCount} logs
              </div>
              <div className="flex items-center gap-3">
                <label htmlFor="items-per-page" className="text-sm text-muted-foreground">Items per page:</label>
                <Select value={String(itemsPerPage)} onValueChange={(val) => {
                  setItemsPerPage(Number(val));
                  setCurrentPage(0);
                }}>
                  <SelectTrigger className="w-[80px] h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Page Navigation */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-sm font-medium">
                Page {currentPage + 1} of {Math.ceil(totalCount / itemsPerPage)}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Previous Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0 || loading}
                  data-testid="audit-prev-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>

                {/* Page Number Buttons */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.ceil(totalCount / itemsPerPage) }).map((_, idx) => {
                    const pageNum = idx;
                    const isCurrentPage = pageNum === currentPage;
                    const isVisible = Math.abs(pageNum - currentPage) <= 2 || pageNum === 0 || pageNum === Math.ceil(totalCount / itemsPerPage) - 1;

                    if (!isVisible) {
                      if (pageNum === 1 && currentPage > 3) return <span key="ellipsis-start" className="px-1">...</span>;
                      return null;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={isCurrentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                        className={isCurrentPage ? "min-w-9" : ""}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                </div>

                {/* Next Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!hasMore || loading}
                  data-testid="audit-next-btn"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
